/**
 * setup contracts — owned by work package api-setup (auth extensions, courses, tournaments,
 * branding, links). Request/response zod schemas of those endpoints so apps/web can import them.
 * Every exported name is prefixed with `Setup` (no collisions with other packages' contracts).
 *
 * Conventions (CLAUDE.md §11.1): ids are UUIDv7, dates 'yyyy-MM-dd', instants ISO 8601, lists are
 * `{ items, nextCursor }`.
 */
import { z } from 'zod';
import {
  CategoryAwardOverrideSchema,
  ScoringConfigSchema,
  type ScoringConfigInput,
} from '../config/scoring';
import { TournamentPoliciesSchema } from '../config/policies';
import { DistanceUnitSchema } from '../config/distance';
import { PreferredTeeSchema } from '../config/preferred-tee';
import {
  CategoryFilterSchema,
  RegistrationSettingsSchema,
  TeeRuleMatchSchema,
} from '../config/registration';
import {
  ActorType,
  AwardType,
  BannerPlacement,
  CategoryDerivation,
  CategoryFieldType,
  ContestType,
  CourseVisibility,
  FormatKind,
  Gender,
  LinkPurpose,
  RoundStatus,
  StartType,
  TenantKind,
  TenantRole,
  TenantStatus,
  TournamentKind,
  TournamentRole,
  TournamentStatus,
  UserStatus,
} from '../enums';
import { I18nText, LocaleSchema } from '../i18n';

// ---------------------------------------------------------------------------------------------
// shared primitives
// ---------------------------------------------------------------------------------------------

/** 'yyyy-MM-dd' calendar date */
export const SetupDateSchema = z.iso.date();
/** local wall-clock time "HH:mm" (tournament timezone) */
export const SetupTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm');
/** "#RRGGBB" */
export const SetupHexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, '#RRGGBB');
/** access level of the caller on a tournament (CLAUDE.md §2.4) */
export const SetupAccessLevel = z.enum(['VIEWER', 'OPERATOR', 'ADMIN']);
export type SetupAccessLevel = z.infer<typeof SetupAccessLevel>;

/** paged list shape `{ items, nextCursor }` (nextCursor = last id, null when exhausted) */
export const setupPage = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

export const SetupListQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Optional free-text reason for audited changes. */
export const SetupReasonSchema = z.string().trim().min(1).max(500);

const password = z.string().min(8).max(200);

// ---------------------------------------------------------------------------------------------
// auth extensions
// ---------------------------------------------------------------------------------------------

/** POST /auth/register — general user sign-up (club members). Returns SessionResponse. */
export const SetupRegisterRequestSchema = z.object({
  email: z.email().max(200),
  password,
  displayName: z.string().trim().min(1).max(120),
  phone: z.string().max(40).nullable().optional(),
  locale: LocaleSchema.optional(),
  deviceId: z.string().max(64).optional(),
});
export type SetupRegisterRequest = z.infer<typeof SetupRegisterRequestSchema>;

/** PATCH /auth/me — returns MeResponse */
export const SetupUpdateMeRequestSchema = z
  .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    locale: LocaleSchema.optional(),
    /** null clears the phone number */
    phone: z.string().max(40).nullable().optional(),
    /** preferred tee box colour key; null = automatic (the tournament's tee rules) */
    preferredTee: PreferredTeeSchema.nullable().optional(),
    /** distance display unit (§15 #487) */
    distanceUnit: DistanceUnitSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type SetupUpdateMeRequest = z.infer<typeof SetupUpdateMeRequestSchema>;

/** POST /auth/password — other sessions of the user are revoked */
export const SetupChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: password,
});

/** Tenant profile — GET/PATCH /tenants/:tenantId */
export const SetupTenantSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  kind: TenantKind,
  status: TenantStatus,
  country: z.string(),
  timezone: z.string(),
  defaultLocale: z.string(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  billingName: z.string().nullable(),
  billingAddress: z.string().nullable(),
  taxCode: z.string().nullable(),
  /** caller's level on the tenant */
  access: SetupAccessLevel,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SetupTenant = z.infer<typeof SetupTenantSchema>;

const optText = (max: number) => z.string().trim().max(max).nullable().optional();
export const SetupTenantUpdateSchema = z
  .object({
    contactName: optText(120),
    contactEmail: z.email().max(200).nullable().optional(),
    contactPhone: optText(40),
    billingName: optText(200),
    billingAddress: optText(500),
    taxCode: optText(40),
    timezone: z.string().min(1).max(64).optional(),
    defaultLocale: LocaleSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type SetupTenantUpdate = z.infer<typeof SetupTenantUpdateSchema>;

export const SetupMemberSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  role: TenantRole,
  createdAt: z.iso.datetime(),
  user: z.object({
    id: z.uuid(),
    email: z.string().nullable(),
    displayName: z.string(),
    phone: z.string().nullable(),
    status: UserStatus,
    lastLoginAt: z.iso.datetime().nullable(),
  }),
});
export type SetupMember = z.infer<typeof SetupMemberSchema>;

/** POST /tenants/:tenantId/members — an existing user (same email) is linked, never re-passworded */
export const SetupMemberCreateSchema = z.object({
  email: z.email().max(200),
  displayName: z.string().trim().min(1).max(120),
  role: TenantRole,
  password: password.optional(),
});
export const SetupMemberCreateResponseSchema = z.object({
  member: SetupMemberSchema,
  /** generated one-time password for a NEW user created without `password` (shown once) */
  temporaryPassword: z.string().nullable(),
  /** false = the email belonged to an existing account that was linked */
  createdUser: z.boolean(),
});
export const SetupMemberUpdateSchema = z.object({ role: TenantRole });

/** reset-password responses (tenant member / tournament staff): shown once */
export const SetupTemporaryPasswordResponseSchema = z.object({
  userId: z.uuid(),
  temporaryPassword: z.string(),
  revokedSessions: z.number(),
});

// ---------------------------------------------------------------------------------------------
// courses
// ---------------------------------------------------------------------------------------------

export const SetupHoleInputSchema = z.object({
  number: z.int().min(1).max(18),
  par: z.int().min(3).max(6),
  strokeIndex: z.int().min(1).max(18),
  strokeIndexWomen: z.int().min(1).max(18).nullable().optional(),
  parWomen: z.int().min(3).max(6).nullable().optional(),
});

export const SetupTeeRatingInputSchema = z.object({
  gender: Gender,
  courseRating: z.number().min(20).max(90),
  slopeRating: z.int().min(55).max(155),
  par: z.int().min(27).max(80),
  frontRating: z.number().min(10).max(45).nullable().optional(),
  frontSlope: z.int().min(55).max(155).nullable().optional(),
  backRating: z.number().min(10).max(45).nullable().optional(),
  backSlope: z.int().min(55).max(155).nullable().optional(),
});

export const SetupHoleDistanceSchema = z.object({
  holeNumber: z.int().min(1).max(18),
  meters: z.int().min(1).max(1000).nullable().optional(),
  yards: z.int().min(1).max(1100).nullable().optional(),
});

export const SetupTeeInputSchema = z.object({
  /** keep an existing tee (PUT): its id stays stable for participants / tee rules / snapshots */
  id: z.uuid().optional(),
  name: z.string().trim().min(1).max(40),
  color: SetupHexColorSchema.nullable().optional(),
  sortOrder: z.int().min(0).max(100).optional(),
  ratings: z.array(SetupTeeRatingInputSchema).max(2).default([]),
  distances: z.array(SetupHoleDistanceSchema).max(18).default([]),
});

function uniqueBy<T>(items: readonly T[], key: (t: T) => unknown): boolean {
  return new Set(items.map(key)).size === items.length;
}

function issue(ctx: z.RefinementCtx, path: Array<string | number>, message: string) {
  ctx.addIssue({ code: 'custom', path, message });
}

function checkStrokeIndexes(
  holes: Array<z.infer<typeof SetupHoleInputSchema>>,
  holeCount: number,
  ctx: z.RefinementCtx,
) {
  if (!uniqueBy(holes, (h) => h.strokeIndex)) issue(ctx, ['holes'], 'strokeIndex must be unique');
  if (holeCount === 18 && holes.some((h) => h.strokeIndex > 18))
    issue(ctx, ['holes'], 'strokeIndex must be 1..18');
  const women = holes.map((h) => h.strokeIndexWomen).filter((v) => v != null);
  if (women.length > 0 && (women.length !== holes.length || new Set(women).size !== women.length))
    issue(ctx, ['holes'], 'strokeIndexWomen must be given for every hole and be unique');
}

function checkTees(
  tees: Array<z.infer<typeof SetupTeeInputSchema>>,
  holeCount: number,
  ctx: z.RefinementCtx,
) {
  if (!uniqueBy(tees, (t) => t.name.toLowerCase()))
    issue(ctx, ['tees'], 'tee names must be unique');
  tees.forEach((t, i) => {
    if (!uniqueBy(t.ratings, (r) => r.gender))
      issue(ctx, ['tees', i, 'ratings'], 'one rating per gender');
    if (!uniqueBy(t.distances, (d) => d.holeNumber))
      issue(ctx, ['tees', i, 'distances'], 'one distance per hole');
    if (t.distances.some((d) => d.holeNumber > holeCount))
      issue(ctx, ['tees', i, 'distances'], 'distance for a hole the course does not have');
  });
}

/** POST /tenants/:tenantId/courses and PUT /courses/:courseId (full replacement) */
export const SetupCourseInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    code: z.string().trim().max(40).nullable().optional(),
    holeCount: z.union([z.literal(9), z.literal(18)]),
    visibility: CourseVisibility.default('SHARED'),
    address: z.string().trim().max(500).nullable().optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
    holes: z.array(SetupHoleInputSchema).min(9).max(18),
    tees: z.array(SetupTeeInputSchema).min(1).max(10),
    /** PUT only: reason recorded when `?force=true` overrides the in-use par/SI protection */
    reason: SetupReasonSchema.optional(),
  })
  .superRefine((c, ctx) => {
    if (c.holes.length !== c.holeCount)
      issue(ctx, ['holes'], `expected ${c.holeCount} holes, got ${c.holes.length}`);
    const numbers = new Set(c.holes.map((h) => h.number));
    if (numbers.size !== c.holes.length || c.holes.some((h) => h.number > c.holeCount))
      issue(ctx, ['holes'], `hole numbers must be 1..${c.holeCount}, each once`);
    checkStrokeIndexes(c.holes, c.holeCount, ctx);
    checkTees(c.tees, c.holeCount, ctx);
  });
export type SetupCourseInput = z.input<typeof SetupCourseInputSchema>;

export const SetupCourseSummarySchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  holeCount: z.number(),
  visibility: CourseVisibility,
  address: z.string().nullable(),
  par: z.number(),
  teeNames: z.array(z.string()),
  archivedAt: z.iso.datetime().nullable(),
  /** the caller is a member of the owning tenant (may edit when TA) */
  owned: z.boolean(),
});
export type SetupCourseSummary = z.infer<typeof SetupCourseSummarySchema>;

export const SetupCourseSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  holeCount: z.number(),
  visibility: CourseVisibility,
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  archivedAt: z.iso.datetime().nullable(),
  owned: z.boolean(),
  holes: z.array(
    z.object({
      id: z.uuid(),
      number: z.number(),
      par: z.number(),
      strokeIndex: z.number(),
      strokeIndexWomen: z.number().nullable(),
      parWomen: z.number().nullable(),
    }),
  ),
  tees: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      color: z.string().nullable(),
      sortOrder: z.number(),
      ratings: z.array(
        z.object({
          gender: Gender,
          courseRating: z.number(),
          slopeRating: z.number(),
          par: z.number(),
          frontRating: z.number().nullable(),
          frontSlope: z.number().nullable(),
          backRating: z.number().nullable(),
          backSlope: z.number().nullable(),
        }),
      ),
      distances: z.array(
        z.object({
          holeNumber: z.number(),
          meters: z.number().nullable(),
          yards: z.number().nullable(),
        }),
      ),
    }),
  ),
  /** rounds that already started on this course (their par/SI is frozen in courseSnapshot) */
  activeRoundCount: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SetupCourse = z.infer<typeof SetupCourseSchema>;

export const SetupCourseCatalogQuerySchema = SetupListQuerySchema.extend({
  q: z.string().trim().max(100).optional(),
  holeCount: z.coerce.number().int().optional(),
});
export const SetupTenantCoursesQuerySchema = z.object({
  includeArchived: z.stringbool().default(false),
});
export const SetupCoursePutQuerySchema = z.object({ force: z.stringbool().default(false) });

// ---------------------------------------------------------------------------------------------
// tournaments & rounds
// ---------------------------------------------------------------------------------------------

export const SetupRoundInputSchema = z.object({
  /** default: next free number */
  roundNo: z.int().min(1).max(20).optional(),
  date: SetupDateSchema,
  /** primary layout (Round.courseId) */
  courseId: z.uuid(),
  /** additional layouts played at the same time (CLAUDE.md §15 #46) */
  extraCourseIds: z.array(z.uuid()).max(5).default([]),
  /** default tournament.holesPerRound */
  holeCount: z.union([z.literal(9), z.literal(18)]).optional(),
  /** 1 (front) or 10 (back nine of an 18-hole course, 9-hole rounds only) */
  firstHole: z.union([z.literal(1), z.literal(10)]).default(1),
  startType: StartType.default('SINGLE_TEE'),
  firstTeeTime: SetupTimeSchema.nullable().optional(),
  intervalMinutes: z.int().min(1).max(60).default(8),
});
export type SetupRoundInput = z.input<typeof SetupRoundInputSchema>;

export const SetupTournamentCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullable().optional(),
    startDate: SetupDateSchema,
    endDate: SetupDateSchema,
    timezone: z.string().min(1).max(64).optional(),
    defaultLocale: LocaleSchema.optional(),
    holesPerRound: z.union([z.literal(9), z.literal(18)]).default(18),
    scoringConfig: ScoringConfigSchema,
    policies: TournamentPoliciesSchema.optional(),
    registration: RegistrationSettingsSchema.optional(),
    rounds: z.array(SetupRoundInputSchema).max(20).default([]),
  })
  .refine((t) => t.endDate >= t.startDate, { path: ['endDate'], message: 'endDate < startDate' });
export type SetupTournamentCreate = z.input<typeof SetupTournamentCreateSchema>;

export const SetupTournamentUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(5000).nullable().optional(),
    startDate: SetupDateSchema.optional(),
    endDate: SetupDateSchema.optional(),
    timezone: z.string().min(1).max(64).optional(),
    defaultLocale: LocaleSchema.optional(),
    /** only while every round is SCHEDULED */
    holesPerRound: z.union([z.literal(9), z.literal(18)]).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');

export const SetupTournamentListQuerySchema = SetupListQuerySchema.extend({
  status: TournamentStatus.optional(),
  kind: TournamentKind.optional(),
  /** startDate >= from */
  from: SetupDateSchema.optional(),
  /** startDate <= to */
  to: SetupDateSchema.optional(),
});

export const SetupTournamentSummarySchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  clubId: z.uuid().nullable(),
  kind: TournamentKind,
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: TournamentStatus,
  formatKind: FormatKind,
  holesPerRound: z.number(),
  roundCount: z.number(),
  participantCount: z.number(),
  createdAt: z.iso.datetime(),
});
export type SetupTournamentSummary = z.infer<typeof SetupTournamentSummarySchema>;

export const SetupRoundSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  roundNo: z.number(),
  date: z.string(),
  courseId: z.uuid(),
  holeCount: z.number(),
  firstHole: z.number(),
  startType: StartType,
  firstTeeTime: z.string().nullable(),
  intervalMinutes: z.number(),
  status: RoundStatus,
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  lockedAt: z.iso.datetime().nullable(),
  hiddenRevealedAt: z.iso.datetime().nullable(),
  /** layouts of the round, primary first */
  courses: z.array(
    z.object({
      courseId: z.uuid(),
      name: z.string(),
      holeCount: z.number(),
      sortOrder: z.number(),
      isPrimary: z.boolean(),
      /** course frozen at round start (RoundCourse.courseSnapshot present) */
      snapshotted: z.boolean(),
    }),
  ),
  groupCount: z.number(),
});
export type SetupRound = z.infer<typeof SetupRoundSchema>;

export const SetupLinkSchema = z.object({
  purpose: LinkPurpose,
  token: z.string(),
  url: z.string(),
  createdAt: z.iso.datetime(),
});
export type SetupLink = z.infer<typeof SetupLinkSchema>;

export const SetupTournamentSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  clubId: z.uuid().nullable(),
  seasonId: z.uuid().nullable(),
  kind: TournamentKind,
  name: z.string(),
  description: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  timezone: z.string(),
  defaultLocale: z.string(),
  status: TournamentStatus,
  holesPerRound: z.number(),
  formatKind: FormatKind,
  /** STORED scoring config (admin choices; "auto" fields omitted) */
  scoringConfig: ScoringConfigSchema,
  /** EFFECTIVE scoring config (normalizeScoringConfig) */
  effectiveScoringConfig: z.record(z.string(), z.unknown()),
  policies: TournamentPoliciesSchema,
  registration: RegistrationSettingsSchema,
  rounds: z.array(SetupRoundSchema),
  counts: z.object({
    participants: z.number(),
    approved: z.number(),
    pending: z.number(),
    waitlisted: z.number(),
    groups: z.number(),
    caddies: z.number(),
    teams: z.number(),
  }),
  /** share links (OPERATOR and above; empty for VIEWER) */
  links: z.array(SetupLinkSchema),
  access: SetupAccessLevel,
  realtimeVersion: z.number(),
  finalizedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SetupTournament = z.infer<typeof SetupTournamentSchema>;

export const SetupDeleteTournamentResponseSchema = z.object({
  /** true = hard-deleted (DRAFT); false = moved to CANCELLED */
  deleted: z.boolean(),
  status: TournamentStatus.nullable(),
});

export const SetupDuplicateTournamentSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  /** new start date; round dates/endDate keep their offsets. Default: same dates */
  startDate: SetupDateSchema.optional(),
  /** also copy branding + sponsor banners (default true) */
  includeBranding: z.boolean().default(true),
});

/** PUT /tournaments/:id/scoring|policies|registration response */
export const SetupScoringResponseSchema = z.object({
  scoringConfig: ScoringConfigSchema,
  effectiveScoringConfig: z.record(z.string(), z.unknown()),
});
export type SetupScoringConfigInput = ScoringConfigInput;

/** POST /tournaments/:id/status (FINALIZED belongs to results) */
export const SetupStatusTarget = z.enum([
  'DRAFT',
  'REGISTRATION_OPEN',
  'REGISTRATION_CLOSED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
]);
export const SetupStatusChangeSchema = z.object({
  to: SetupStatusTarget,
  reason: SetupReasonSchema.optional(),
});
export const SetupStatusChangeResponseSchema = z.object({
  id: z.uuid(),
  status: TournamentStatus,
  previous: TournamentStatus,
});

export const SetupRoundUpdateSchema = z
  .object({
    date: SetupDateSchema.optional(),
    firstTeeTime: SetupTimeSchema.nullable().optional(),
    intervalMinutes: z.int().min(1).max(60).optional(),
    startType: StartType.optional(),
    /** SCHEDULED only */
    courseId: z.uuid().optional(),
    holeCount: z.union([z.literal(9), z.literal(18)]).optional(),
    firstHole: z.union([z.literal(1), z.literal(10)]).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');

/** PUT /rounds/:roundId/courses — full layout replacement (must contain the primary course) */
export const SetupRoundCoursesSchema = z.object({
  courses: z
    .array(z.object({ courseId: z.uuid(), sortOrder: z.int().min(0).max(100).optional() }))
    .min(1)
    .max(6)
    .refine((c) => uniqueBy(c, (x) => x.courseId), 'duplicate courseId'),
});

// ---- categories ------------------------------------------------------------------------------

export const SetupCategoryOptionInputSchema = z.object({
  value: z.string().trim().min(1).max(60),
  label: I18nText,
  minValue: z.number().nullable().optional(),
  maxValue: z.number().nullable().optional(),
  sortOrder: z.int().min(0).max(1000).optional(),
});

const optionList = z
  .array(SetupCategoryOptionInputSchema)
  .max(100)
  .refine((o) => uniqueBy(o, (x) => x.value), 'duplicate option value');

export const SetupCategoryCreateSchema = z.object({
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/),
  label: I18nText,
  fieldType: CategoryFieldType.default('SELECT'),
  derivation: CategoryDerivation.default('NONE'),
  required: z.boolean().default(false),
  showInSignup: z.boolean().default(true),
  showInFilter: z.boolean().default(true),
  awardRanking: z.boolean().default(false),
  awardConfig: CategoryAwardOverrideSchema.nullable().optional(),
  sortOrder: z.int().min(0).max(1000).optional(),
  /** GENDER / PRO_AM: omitted -> standard options (M/F, PRO/AMATEUR) are created */
  options: optionList.default([]),
});

export const SetupCategoryUpdateSchema = z
  .object({
    label: I18nText.optional(),
    fieldType: CategoryFieldType.optional(),
    derivation: CategoryDerivation.optional(),
    required: z.boolean().optional(),
    showInSignup: z.boolean().optional(),
    showInFilter: z.boolean().optional(),
    awardRanking: z.boolean().optional(),
    awardConfig: CategoryAwardOverrideSchema.nullable().optional(),
    sortOrder: z.int().min(0).max(1000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');

export const SetupCategoryOptionsSchema = z.object({ options: optionList });

export const SetupCategorySchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  key: z.string(),
  label: I18nText,
  fieldType: CategoryFieldType,
  derivation: CategoryDerivation,
  required: z.boolean(),
  showInSignup: z.boolean(),
  showInFilter: z.boolean(),
  awardRanking: z.boolean(),
  awardConfig: CategoryAwardOverrideSchema.nullable(),
  sortOrder: z.number(),
  options: z.array(
    z.object({
      id: z.uuid(),
      value: z.string(),
      label: I18nText,
      minValue: z.number().nullable(),
      maxValue: z.number().nullable(),
      sortOrder: z.number(),
    }),
  ),
});
export type SetupCategory = z.infer<typeof SetupCategorySchema>;

// ---- tee rules -------------------------------------------------------------------------------

export const SetupTeeRuleInputSchema = z.object({
  /** null = every round */
  roundId: z.uuid().nullable().optional(),
  teeBoxId: z.uuid(),
  /** lower = evaluated first */
  priority: z.int().min(0).max(10000).default(100),
  /** AND across keys, OR within values. Keys: category keys or the built-in `gender` */
  match: TeeRuleMatchSchema,
});
export const SetupTeeRulesPutSchema = z.object({
  rules: z.array(SetupTeeRuleInputSchema).max(100),
});
export const SetupTeeRuleSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  roundId: z.uuid().nullable(),
  teeBoxId: z.uuid(),
  teeBoxName: z.string(),
  courseId: z.uuid(),
  priority: z.number(),
  match: TeeRuleMatchSchema,
});
export type SetupTeeRule = z.infer<typeof SetupTeeRuleSchema>;

// ---- special contests (nearest pin / longest drive) -----------------------------------------

export const SetupContestCreateSchema = z.object({
  type: ContestType,
  holeNumber: z.int().min(1).max(18),
  /** null = every round (one leader per round) */
  roundId: z.uuid().nullable().optional(),
  /** layout of the hole; required when a round of the contest plays several layouts */
  courseId: z.uuid().nullable().optional(),
  name: z.string().trim().max(120).nullable().optional(),
  /** category key -> allowed values (null/empty = everyone) */
  eligibility: CategoryFilterSchema.nullable().optional(),
  prize: z.string().trim().max(500).nullable().optional(),
  sortOrder: z.int().min(0).max(1000).optional(),
});
export const SetupContestUpdateSchema = z
  .object({
    type: ContestType.optional(),
    holeNumber: z.int().min(1).max(18).optional(),
    roundId: z.uuid().nullable().optional(),
    courseId: z.uuid().nullable().optional(),
    name: z.string().trim().max(120).nullable().optional(),
    eligibility: CategoryFilterSchema.nullable().optional(),
    prize: z.string().trim().max(500).nullable().optional(),
    sortOrder: z.int().min(0).max(1000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');

export const SetupContestSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  type: ContestType,
  holeNumber: z.number(),
  roundId: z.uuid().nullable(),
  roundNo: z.number().nullable(),
  courseId: z.uuid().nullable(),
  name: z.string().nullable(),
  eligibility: CategoryFilterSchema.nullable(),
  prize: z.string().nullable(),
  sortOrder: z.number(),
  /** hole par on the contest's layout(s) (null when it differs between layouts) */
  par: z.number().nullable(),
  entryCount: z.number(),
});
export type SetupContest = z.infer<typeof SetupContestSchema>;

/** create/update response: the contest + non-blocking warnings (e.g. NEAREST_PIN_NOT_PAR3) */
export const SetupContestMutationResponseSchema = z.object({
  contest: SetupContestSchema,
  warnings: z.array(z.string()),
});

// ---- special awards --------------------------------------------------------------------------

export const SetupAwardCreateSchema = z.object({
  type: AwardType,
  name: z.string().trim().min(1).max(120),
  /** null = any hole (MOST_BIRDIES/CUSTOM ignore) */
  holeNumber: z.int().min(1).max(18).nullable().optional(),
  roundId: z.uuid().nullable().optional(),
  prize: z.string().trim().max(500).nullable().optional(),
  /** CUSTOM awards are never auto-detected */
  autoDetect: z.boolean().default(true),
  sortOrder: z.int().min(0).max(1000).optional(),
});
export const SetupAwardUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    holeNumber: z.int().min(1).max(18).nullable().optional(),
    roundId: z.uuid().nullable().optional(),
    prize: z.string().trim().max(500).nullable().optional(),
    autoDetect: z.boolean().optional(),
    sortOrder: z.int().min(0).max(1000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');

export const SetupAwardWinnerSchema = z.object({
  id: z.uuid(),
  participantId: z.uuid(),
  participantName: z.string(),
  bibNumber: z.number().nullable(),
  /** 0 = not round-specific */
  roundNo: z.number(),
  /** 0 = not hole-specific */
  holeNumber: z.number(),
  strokes: z.number().nullable(),
  confirmed: z.boolean(),
  confirmedById: z.uuid().nullable(),
  note: z.string().nullable(),
  detectedAt: z.iso.datetime(),
  /**
   * Provenance of the qualifying score (GET /tournaments/:tid/awards; absent on mutation replies):
   * server time of the latest score audit row that wrote this hole's value, null when the award is
   * not hole-specific or no audit row matches. Only HOLE_IN_ONE / ALBATROSS / EAGLE winners carry
   * these fields (unconfirmed first, at most 200 per list); other winners omit them.
   */
  enteredAt: z.iso.datetime().nullable().optional(),
  /** who wrote that value: caddy code + name, admin display name, or the self-entry player */
  enteredBy: z
    .object({
      actorType: ActorType,
      caddyCode: z.string().nullable(),
      name: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /** an IMPROBABLE_SCORE anomaly flag (not dismissed) on the same cell, if any */
  anomalyId: z.uuid().nullable().optional(),
});
export const SetupAwardSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  type: AwardType,
  name: z.string(),
  holeNumber: z.number().nullable(),
  roundId: z.uuid().nullable(),
  prize: z.string().nullable(),
  autoDetect: z.boolean(),
  sortOrder: z.number(),
  winners: z.array(SetupAwardWinnerSchema),
});
export type SetupAward = z.infer<typeof SetupAwardSchema>;

export const SetupAwardConfirmSchema = z.object({
  note: z.string().trim().max(500).nullable().optional(),
  /** false withdraws a confirmation */
  confirmed: z.boolean().default(true),
});

// ---- tournament staff ------------------------------------------------------------------------

export const SetupStaffCreateSchema = z.object({
  email: z.email().max(200),
  role: TournamentRole,
  /** used when a new account is created (default: the email's local part) */
  displayName: z.string().trim().min(1).max(120).optional(),
});
export const SetupStaffSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  role: TournamentRole,
  createdAt: z.iso.datetime(),
  user: z.object({
    id: z.uuid(),
    email: z.string().nullable(),
    displayName: z.string(),
    status: UserStatus,
  }),
});
export type SetupStaff = z.infer<typeof SetupStaffSchema>;
export const SetupStaffCreateResponseSchema = z.object({
  staff: SetupStaffSchema,
  temporaryPassword: z.string().nullable(),
  createdUser: z.boolean(),
});

// ---------------------------------------------------------------------------------------------
// branding, sponsors, assets
// ---------------------------------------------------------------------------------------------

export const SetupBrandingSchema = z.object({
  tournamentId: z.uuid(),
  logoAssetId: z.uuid().nullable(),
  logoUrl: z.string().nullable(),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  accentColor: z.string(),
  headline: I18nText.nullable(),
  footerText: I18nText.nullable(),
  tvScrollSeconds: z.number(),
});
export type SetupBranding = z.infer<typeof SetupBrandingSchema>;

export const SetupBrandingUpdateSchema = z.object({
  logoAssetId: z.uuid().nullable().optional(),
  primaryColor: SetupHexColorSchema,
  secondaryColor: SetupHexColorSchema,
  accentColor: SetupHexColorSchema,
  headline: I18nText.nullable().optional(),
  footerText: I18nText.nullable().optional(),
  tvScrollSeconds: z.int().min(3).max(120).default(8),
});

const httpUrl = z
  .url({ protocol: /^https?$/ })
  .max(1000)
  .nullable()
  .optional();

export const SetupSponsorCreateSchema = z.object({
  assetId: z.uuid(),
  name: z.string().trim().min(1).max(120),
  linkUrl: httpUrl,
  placement: BannerPlacement.default('LEADERBOARD_TOP'),
  sortOrder: z.int().min(0).max(1000).optional(),
  active: z.boolean().default(true),
});
export const SetupSponsorUpdateSchema = z
  .object({
    assetId: z.uuid().optional(),
    name: z.string().trim().min(1).max(120).optional(),
    linkUrl: httpUrl,
    placement: BannerPlacement.optional(),
    sortOrder: z.int().min(0).max(1000).optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const SetupSponsorSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  assetId: z.uuid(),
  imageUrl: z.string(),
  name: z.string(),
  linkUrl: z.string().nullable(),
  placement: BannerPlacement,
  sortOrder: z.number(),
  active: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type SetupSponsor = z.infer<typeof SetupSponsorSchema>;

/** POST /tenants/:tenantId/assets (multipart field `file`; png/jpeg/webp <= 5 MB) */
export const SetupAssetSchema = z.object({
  id: z.uuid(),
  url: z.string(),
  fileName: z.string(),
  mimeType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  sizeBytes: z.number(),
  sha256: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});
export type SetupAsset = z.infer<typeof SetupAssetSchema>;
export const SETUP_ASSET_MAX_BYTES = 5 * 1024 * 1024;

// ---------------------------------------------------------------------------------------------
// links
// ---------------------------------------------------------------------------------------------

export const SetupLinkPurposeParamSchema = z.object({
  tournamentId: z.uuid(),
  purpose: LinkPurpose,
});
export const SetupQrQuerySchema = z.object({
  format: z.enum(['png', 'svg']).default('png'),
  /** pixel width of the PNG / SVG viewport */
  size: z.coerce.number().int().min(128).max(2048).default(512),
});
